import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "@material-ui/core";
import { AttachFile } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import NewCompaignForm from "../../Marketing/NewCompaignForm";
import { launchesActions } from "../../../../../store/launches/launchesActions";
import { conversationAction } from "../../../../../store/conversations/conversationAction";
import ConversationMediaUploadPopup from "../../../../../components/common/Conversation/ConversationMediaUploadPopup";
import { emailTemplateActions } from "../../../../../store/emailTemplate/emailTemplateActions";
import { dashboardActions } from "../../../../../store/dashboard/dashboardActions";

const useStyles = makeStyles(theme => ({
  container: {
    padding: "20px",
  },
  attachmentSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    margin: "20px 0",
    gap: "10px",
  },
  fileName: {
    color: "#3699ff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
  },
  attachmentIcon: {
    cursor: "pointer",
    color: "#333333",
  },
  sendButton: {
    marginTop: "20px",
  },
}));

const ProspectEmailTab = ({
  prospectData,
  setOpen,
  setLoading,
  loadAllCampaigns,
  loadLeadsGeneration,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    subject: "",
    model: "",
    selectedTemplateId: null,
  });
  const [uploadData, setUploadData] = useState({
    isUpload: false,
    uploadFile: "",
    fileName: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [roomName, setRoomName] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);

  // Fetch email templates on component mount
  useEffect(() => {
    if (
      prospectData?.platform !== "Facebook" &&
      prospectData?.platform !== "Instagram"
    ) {
      fetchEmailTemplates();
    }
  }, [prospectData]);

  // Function to fetch email templates
  const fetchEmailTemplates = useCallback(async () => {
    try {
      const result = await dispatch(
        emailTemplateActions.onGetTemplatePaginatedData({
          pagination: {
            pageSize: 1000,
            pageNo: 1,
            orderByKey: "Id",
            orderByVal: 0,
          },
          templateName: "",
          subject: "",
          body: "",
          search: "",
          DateFrom: null,
          DateTo: null,
        }),
      );
      if (result?.data?.success) {
        setEmailTemplates(result.data?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
    }
  }, [dispatch]);

  // Update form data when prospect changes
  const updateFormData = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  // Add this function to handle template selection
  const handleTemplateSelect = useCallback(
    templateId => {
      if (!templateId) {
        // Clear form if "Select Template" is chosen
        setFormData(prev => ({
          ...prev,
          selectedTemplateId: null,
          subject: "",
          model: "",
        }));
        return;
      }

      const selectedTemplate = emailTemplates.find(
        template => template.id === templateId,
      );
      if (selectedTemplate) {
        setFormData(prev => ({
          ...prev,
          selectedTemplateId: templateId,
          subject: selectedTemplate.subject,
          model: selectedTemplate.body,
        }));
      }
    },
    [emailTemplates],
  );

  const updateUploadData = useCallback((key, value) => {
    setUploadData(prev => ({ ...prev, [key]: value }));
  }, []);

  // Check if email can be sent
  const canSendEmail = useCallback(() => {
    const { subject, model } = formData;

    if (
      prospectData?.platform === "Facebook" ||
      prospectData?.platform === "Instagram"
    ) {
      return !!(uploadData.fileName || model);
    }

    return !!(subject && model);
  }, [formData, uploadData, prospectData]);

  // Upload attachments
  const uploadAttachments = useCallback(async () => {
    if (!uploadData.fileName) return [];

    const savedAttachments = await dispatch(
      conversationAction.onUploadFile(uploadData.uploadFile),
    );

    if (savedAttachments?.responseCode === 1) {
      return [
        {
          name: uploadData.fileName,
          url: savedAttachments.responseData?.url,
        },
      ];
    }

    return [];
  }, [dispatch, uploadData]);

  // Submit email campaign
  const submitEmailCampaign = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        messageId: 0,
        subject: formData.subject,
        body: formData.model,
        toEmails: prospectData?.email,
        replyAll: false,
        attachments: attachments,

        // subject: formData.subject,
        // body: formData.model,
        // groupType: "",
        // type: "email",
        // isDeleted: true,
        // businessUsers: [],
        // campaignEndUsers: [],
        // campaignLeadUsers: [],
        // campaignProspects: [prospectData],
        // attachments: attachments,
      };

      const result = await dispatch(dashboardActions.onSendEmail(payload));

      if (result?.data?.success) {
        loadLeadsGeneration();
        setOpen(false);
        toast.success(result?.data?.message);
      } else {
        toast.error(result?.data?.message);
      }
    } catch (error) {
      toast.error("Failed to send email");
      console.error("Email submission error:", error);
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    prospectData,
    dispatch,
    setLoading,
    loadAllCampaigns,
    setOpen,
    attachments,
    loadLeadsGeneration,
  ]);

  // Submit social media message
  const submitSocialMedia = useCallback(async () => {
    setLoading(true);

    try {
      const cleanMessage =
        formData.model
          ?.replace(/<br\s*\/?>/gi, "\n")
          ?.replace(/&nbsp;/g, " ")
          ?.replace(/<[^>]*>/g, "")
          .trim() || "";

      let attachments = [];
      if (uploadData.fileName) {
        attachments = await uploadAttachments();
      }

      const attachmentType =
        uploadData.uploadFile.length === 0
          ? ""
          : uploadData.uploadFile[0]?.type?.indexOf("image") > -1
            ? "image"
            : "file";

      const action =
        prospectData?.platform === "Facebook"
          ? conversationAction.onSendMessages
          : conversationAction.onSendInstaMessages;

      const payload =
        prospectData?.platform === "Facebook"
          ? {
              messageFrom: "Aquatic AV",
              messageTo: roomName,
              message: cleanMessage,
              attachmentUrl: attachments.length ? attachments[0].url : "",
              attachmentType,
            }
          : {
              messageFrom: "Aquatic AV",
              messageTo: prospectData?.roomId,
              message: cleanMessage,
              attachmentUrl: attachments.length ? attachments[0].url : "",
              attachmentType,
            };

      const result = await dispatch(action(payload));

      if (result?.responseMsg === "Success") {
        loadAllCampaigns();
        setOpen(false);
        toast.success("Message sent successfully");
      } else {
        toast.error(result?.responseMsg || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
      console.error("Social media submission error:", error);
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    uploadData,
    prospectData,
    roomName,
    dispatch,
    uploadAttachments,
    setLoading,
    loadAllCampaigns,
    setOpen,
  ]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!canSendEmail()) return;

    if (prospectData?.platform === "Facebook") {
      await submitSocialMedia();
    } else if (prospectData?.platform === "Instagram") {
      await submitSocialMedia();
    } else {
      await submitEmailCampaign();
    }
  }, [prospectData, canSendEmail, submitSocialMedia, submitEmailCampaign]);

  // Get room name for Facebook
  const getRoomByRoomId = useCallback(() => {
    if (!prospectData?.roomId) return;

    dispatch(conversationAction.onGetRoomByRId(prospectData.roomId)).then(
      data => {
        if (data?.responseMsg === "Success") {
          setRoomName(data?.responseData?.name);
        }
      },
    );
  }, [dispatch, prospectData]);

  useEffect(() => {
    if (prospectData?.platform === "Facebook") {
      getRoomByRoomId();
    }
  }, [prospectData, getRoomByRoomId]);

  return (
    <div className={classes.container}>
      <NewCompaignForm
        open={prospectData}
        endUserCampaign={[]}
        setEndUserCampaign={() => {}}
        businessCustomerCampaign={[]}
        setBusinessCustomerCampaign={() => {}}
        leadCampaign={[prospectData?.id]}
        setLeadCampaign={() => {}}
        model={formData.model}
        setModel={value => updateFormData("model", value)}
        subject={formData.subject}
        setSubject={value => updateFormData("subject", value)}
        isSupportInbox={false}
        emailTemplates={emailTemplates}
        selectedTemplateId={formData.selectedTemplateId}
        onTemplateSelect={handleTemplateSelect}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        showAttachments={true}
      />

      {uploadData.isUpload && (
        <ConversationMediaUploadPopup
          isMultiple={false}
          openPopup={uploadData.isUpload}
          closePopup={() => updateUploadData("isUpload", false)}
          fileName={uploadData.fileName}
          setFileInfo={(name, file) => {
            updateUploadData("fileName", name);
            updateUploadData("uploadFile", file);
          }}
        />
      )}

      {(prospectData?.platform === "Facebook" ||
        prospectData?.platform === "Instagram") && (
        <div className={classes.attachmentSection}>
          <div className={classes.fileName}>{uploadData.fileName}</div>
          <AttachFile
            className={classes.attachmentIcon}
            onClick={() => updateUploadData("isUpload", true)}
          />
        </div>
      )}

      <Button
        color='primary'
        variant='contained'
        disabled={!canSendEmail()}
        onClick={handleSubmit}
        className={classes.sendButton}
        fullWidth>
        Send
      </Button>
    </div>
  );
};

export default ProspectEmailTab;
